import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';

import DownloadIcon from 'assets/svg/DownloadIcon';
import AppBreadcrumb from 'components/molecules/common/AppBreadcrumb';
import { colorConstants } from 'config/colorConstants';
import {
    PPA_PACK_ARCH_BACK_BTN,
    PPA_PACK_ARCH_EXPORT_BTN,
    PPA_PACK_ARCH_INFO_BTN,
    PPA_PACK_ARCH_TITLE,
} from 'constants/ppaTestIds';

const splitPackArchitectureTooltipLines = (text: string) =>
    text
        .replace(/\\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

const PackArchitectureInfoTooltipContent = ({ text }: { text: string }) => {
    const lines = splitPackArchitectureTooltipLines(text);

    if (lines.length <= 1) {
        return <span className="block text-sm leading-[22px]">{lines[0] ?? text}</span>;
    }

    const [intro, ...bullets] = lines;

    return (
        <div className="max-w-[320px] text-sm leading-[22px]">
            <p className="m-0 mb-2">{intro}</p>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2">
                        <span className="shrink-0 leading-[22px]" aria-hidden>
                            •
                        </span>
                        <span>{bullet}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

type PackArchitecturePageHeaderProps = {
    title: string;
    tooltip: string;
    onDownloadClick?: () => void;
    downloadDisabled?: boolean;
    isDownloadOpen?: boolean;
};

const PackArchitecturePageHeader = ({
    title,
    tooltip,
    onDownloadClick,
    downloadDisabled = false,
    isDownloadOpen = false,
}: PackArchitecturePageHeaderProps) => {
    const navigate = useNavigate();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    useEffect(() => {
        const closeTooltipOnScroll = () => {
            setIsTooltipOpen(false);
        };

        window.addEventListener('scroll', closeTooltipOnScroll, true);

        return () => {
            window.removeEventListener('scroll', closeTooltipOnScroll, true);
        };
    }, []);

    return (
        <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                    aria-label="Go back"
                    data-testid={PPA_PACK_ARCH_BACK_BTN}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z"
                            fill={colorConstants.headerColor}
                        />
                    </svg>
                </button>
                <h1
                    className="m-0 text-[20px] font-semibold leading-8 tracking-[0.5px]"
                    style={{ color: colorConstants.blackTitle }}
                    data-testid={PPA_PACK_ARCH_TITLE}
                >
                    {title}
                </h1>
                {tooltip ? (
                    <Tooltip
                        open={isTooltipOpen}
                        onOpenChange={setIsTooltipOpen}
                        placement="right"
                        color={colorConstants.blackAlt1}
                        title={<PackArchitectureInfoTooltipContent text={tooltip} />}
                        overlayInnerStyle={{ padding: '8px 12px' }}
                    >
                        <InfoCircleOutlined
                            className="inline-flex cursor-pointer items-center justify-center text-[16px] leading-none"
                            style={{ color: colorConstants.greyAlt14 }}
                            data-testid={PPA_PACK_ARCH_INFO_BTN}
                        />
                    </Tooltip>
                ) : null}
            </div>
            <button
                type="button"
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-borderGreyAl1 bg-transparent p-0 text-toolbarLabelGrey shadow-none hover:border-purpleFontSelected hover:bg-transparent hover:text-toolbarLabelGrey active:border-purpleFontPressed disabled:cursor-not-allowed disabled:text-toolbarLabelGrey ${
                    isDownloadOpen ? '!border-purpleFontPressed' : ''
                }`}
                aria-label="Download"
                disabled={downloadDisabled}
                onClick={onDownloadClick}
                data-testid={PPA_PACK_ARCH_EXPORT_BTN}
            >
                <DownloadIcon stroke="currentColor" width={14} height={14} />
            </button>
        </div>
    );
};

const PackArchitectureBreadcrumb = ({
    title,
    groupLabel = 'Opportunity Assessment',
}: {
    title: string;
    groupLabel?: string;
}) => (
    <AppBreadcrumb items={[{ label: groupLabel }, { label: title }]} showHomeIcon={false} />
);

export { PackArchitectureBreadcrumb, PackArchitecturePageHeader };
